import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["identifier", "__isNew__"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { find, has, isArray, isObject } from 'underscore';
import PropTypes from 'prop-types';
import moment from 'moment';
import React from 'react';
import SinglePicker from '../../common/controls/singlePicker';
import { leafPedigreesString } from '../../../../behaviors/src/lib/common';
import { removeNulls } from '../../../utils/contentTools';
import localChangeSelector from '../selectors/localChangeSelector';

/**
 * Note that this is not a connected component. optionPickerField and componentCodePicker are both
 * connected and are the only code that create OptionSinglePicker
 */
/* eslint-disable no-underscore-dangle */
const OptionSinglePicker = _ref => {
  let _ref$allowsClear = _ref.allowsClear,
    allowsClear = _ref$allowsClear === void 0 ? true : _ref$allowsClear,
    _ref$allowsOther = _ref.allowsOther,
    allowsOther = _ref$allowsOther === void 0 ? false : _ref$allowsOther,
    _ref$className = _ref.className,
    inClassName = _ref$className === void 0 ? '' : _ref$className,
    contentPath = _ref.contentPath,
    _ref$enabled = _ref.enabled,
    enabled = _ref$enabled === void 0 ? true : _ref$enabled,
    _ref$labelKey = _ref.labelKey,
    labelKey = _ref$labelKey === void 0 ? 'title' : _ref$labelKey,
    onChange = _ref.onChange,
    options = _ref.options,
    path = _ref.path,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? '' : _ref$testingIdentifie,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    _ref$valueKey = _ref.valueKey,
    valueKey = _ref$valueKey === void 0 ? 'identifier' : _ref$valueKey;
  const localChangeFunc = localChangeSelector({}, {
    allowsClear,
    allowsOther,
    className: inClassName,
    contentPath,
    enabled,
    labelKey,
    onChange,
    options,
    path,
    placeholder,
    testingIdentifier,
    value,
    valueKey
  });

  /**
   *  @function handleChange - transforms user selected data.
   */
  const handleChange = incomingValue => {
    if (incomingValue) {
      const identifier = incomingValue.identifier,
        __isNew__ = incomingValue.__isNew__,
        rest = _objectWithoutProperties(incomingValue, _excluded);
      if (!rest._created) {
        rest._created = new Date().toISOString();
      }
      const v = {
        [identifier]: rest
      };
      const d = JSON.parse(JSON.stringify(v));
      const displayValue = leafPedigreesString(removeNulls(d));
      onChange(localChangeFunc({
        path,
        value: v,
        displayValue
      }));
    } else {
      onChange(localChangeFunc({
        path,
        value: null
      }));
    }
  };

  /**
   *  @function prepareValue - transforms data coming from server.
   */
  const prepareValue = () => {
    let transformedValue = '';
    let id = '';
    if (isArray(value)) {
      transformedValue = '';
    }
    if (isObject(value) && value !== null && value[Object.keys(value)[0]] !== null) {
      const key = Object.keys(value)[0];
      id = key;
      transformedValue = (value[key] || {})[labelKey];
    }
    return {
      value: transformedValue,
      id
    };
  };

  /**
   * @function prepareOptions - sorts options by _created field;
   *
   */
  const prepareOptions = () => {
    // check options array for existence of valueKey and labelKey fields.
    // if the valueKey field (ex. identifier) is missing,
    // create a valueKey field and assign it a value derived from labelKey.
    //
    // Also add a _created property to each option
    // based its order in the options array.
    const rawOptions = JSON.parse(JSON.stringify(options));
    const sortedOptions = [];

    // add value to options if it doesn't exist. this covers cases where a value is written by
    // a behavior and the options list is empty or does not contain the written value
    const optionsHasValue = opt => opt.identifier && value[opt.identifier];
    if (value && (rawOptions.length === 0 || !rawOptions.some(optionsHasValue))) {
      Object.keys(value).forEach(key => {
        const data = value[key];
        rawOptions.push(_objectSpread(_objectSpread({}, data), {}, {
          identifier: key
        }));
      });
    }
    rawOptions.forEach((option, index) => {
      let newOption = {};
      newOption = option;
      if (!has(newOption, '_created')) {
        newOption._created = index + 1;
      }
      if (!has(newOption, [valueKey])) {
        newOption[valueKey] = "_".concat(newOption[labelKey]);
      }
      sortedOptions.push(newOption);
    });
    return sortedOptions;
  };

  /**
   * @function prepareOtherOptions - adds an "other" value to the
   *                      options list so it may be shown as selected.
   */
  const prepareOtherOptions = () => {
    let optionsWithOther = prepareOptions();
    const prepared = prepareValue(value);
    const otherValue = prepared.value;
    const id = prepared.id;
    // handles adding in "other" value to the options list
    const foundOption = find(optionsWithOther, option => option[valueKey] === id);
    // include the value if it is NOT found in the option list.
    if (!foundOption && otherValue) {
      optionsWithOther = optionsWithOther.concat({
        [valueKey]: id,
        [labelKey]: otherValue,
        _created: moment().toISOString()
      });
    }
    return optionsWithOther;
  };
  let preparedOptions = [];
  let className = "".concat(inClassName, " ");
  if (allowsOther) {
    preparedOptions = prepareOtherOptions();
    className += testingIdentifier !== '' ? " t-osp t-osp-".concat(testingIdentifier, " ") : ' t-osp ';
  } else {
    preparedOptions = prepareOptions();
    className += testingIdentifier !== '' ? " t-osp t-osp-".concat(testingIdentifier, " ") : ' t-osp ';
  }
  return /*#__PURE__*/React.createElement(SinglePicker, {
    className: className,
    labelKey: labelKey,
    onChange: handleChange,
    options: preparedOptions,
    value: prepareValue().id,
    valueKey: valueKey,
    returnValueKey: null,
    allowCreate: allowsOther || false,
    disabled: !enabled,
    verticalPadding: false,
    placeholder: placeholder,
    clearable: allowsClear
  });
};
OptionSinglePicker.propTypes = {
  allowsClear: PropTypes.bool,
  allowsOther: PropTypes.bool,
  className: PropTypes.string,
  contentPath: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  labelKey: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  path: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  testingIdentifier: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({}), PropTypes.array]),
  valueKey: PropTypes.string
};
export default OptionSinglePicker;